// src/services/otpService.js
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

const toError = (error) => {
  const message = error?.message || error?.toString() || "OTP service error";
  const err = new Error(message);
  err.code = error?.code;
  err.success = false;
  return err;
};

/**
 * OTP Service for Firebase Cloud Functions
 * Provides easy-to-use methods for sending and verifying OTPs
 */

// Available use cases for OTP
export const OTP_USE_CASES = {
  REGISTRATION: "REGISTRATION",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
  PASSKEY_RESETTING: "PASSKEY_RESETTING",
  SEGMENT_VERIFICATION: "SEGMENT_VERIFICATION",
  PASSWORD_RESETTING: "PASSWORD_RESETTING",
  MOBILE_CHANGE: "MOBILE_CHANGE",
  OFFICER_BUYER_VERIFICATION: "OFFICER_BUYER_VERIFICATION",
  OFFICER_OPERATOR_VERIFICATION: "OFFICER_OPERATOR_VERIFICATION",
  OFFICER_SELLER_REGISTRATION_VERIFICATION: "OFFICER_SELLER_REGISTRATION_VERIFICATION",
  SEGMENT_REGISTRATION_BUYER_OPERATOR: "SEGMENT_REGISTRATION_BUYER_OPERATOR",
  SEGMENT_REGISTRATION_OPERATOR_OFFICER: "SEGMENT_REGISTRATION_OPERATOR_OFFICER",
  SEGMENT_REGISTRATION_SELLER_OPERATOR: "SEGMENT_REGISTRATION_SELLER_OPERATOR",
  BUYER_PAYMENT_SETTLEMENT_JRE: "BUYER_PAYMENT_SETTLEMENT_JRE",
  SELLER_STOCK_ISSUE_JRE: "SELLER_STOCK_ISSUE_JRE",
  OPERATOR_STOCK_ISSUE_BUYER_JRE: "OPERATOR_STOCK_ISSUE_BUYER_JRE",
  DELIVERY_VERIFICATION: "DELIVERY_VERIFICATION",
  OPERATOR_PAYMENT_ISSUE_SELLER_JRE: "OPERATOR_PAYMENT_ISSUE_SELLER_JRE",
  SELLER_JRE_PAYMENT_RECEIPT: "SELLER_JRE_PAYMENT_RECEIPT",
  OPERATOR_GOODS: "OPERATOR_GOODS",
  OPERATOR_PAYMENT: "OPERATOR_PAYMENT",
  LOGIN: "LOGIN",
  RESET_PASSWORD: "RESET_PASSWORD",
};

/**
 * Send OTP to a mobile number
 * @param {Object} options - OTP sending options
 * @param {string} options.mobile - Mobile number (10 digits)
 * @param {string} options.useCase - Use case from OTP_USE_CASES
 * @param {number} [options.validity=10] - OTP validity in minutes
 * @param {Object} [options.params={}] - Additional parameters (e.g., PARTY_ID, SETTLEMENT_ID)
 * @returns {Promise<Object>} Response with otpId, mobile, expiresAt, etc.
 */
export const sendOTP = async ({ mobile, useCase, validity = 10, params = {} }) => {
  try {
    const sendOTPFunction = httpsCallable(functions, "sendOTP");

    const response = await sendOTPFunction({
      mobile,
      useCase,
      validity,
      params,
    });

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw toError(error);
  }
};

/**
 * Verify OTP
 * @param {Object} options - OTP verification options
 * @param {string} options.otpId - OTP ID received from sendOTP
 * @param {string} options.otp - OTP code entered by user
 * @returns {Promise<Object>} Response with success status
 */
export const verifyOTP = async ({ otpId, otp }) => {
  try {
    const verifyOTPFunction = httpsCallable(functions, "verifyOTP");

    const response = await verifyOTPFunction({
      otpId,
      otp,
    });

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw toError(error);
  }
};

/**
 * Verify OTP using mobile and useCase (alternative method)
 * @param {Object} options - OTP verification options
 * @param {string} options.mobile - Mobile number
 * @param {string} options.useCase - Use case from OTP_USE_CASES
 * @param {string} options.otp - OTP code entered by user
 * @returns {Promise<Object>} Response with success status
 */
export const verifyOTPByMobile = async ({ mobile, useCase, otp }) => {
  try {
    const verifyOTPFunction = httpsCallable(functions, "verifyOTP");

    const response = await verifyOTPFunction({
      mobile,
      useCase,
      otp,
    });

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw toError(error);
  }
};

/**
 * Login function using mobile and password
 * @param {Object} options - Login options
 * @param {string} options.mobile - Mobile number
 * @param {string} options.password - Password
 * @param {string} options.sellerId - Seller ID (optional, for specific user)
 * @returns {Promise<Object>} Response with success status and user data
 */
export const login = async ({ mobile, password, sellerId }) => {
  try {
    // Send login OTP via Cloud Function
    const sendResponse = await sendOTP({
      mobile,
      useCase: "LOGIN",
      validity: 10,
      params: {},
    });

    return {
      success: true,
      message: "OTP sent for login verification",
      otpId: sendResponse.data.otpId,
      mobile: sendResponse.data.mobile,
    };
  } catch (error) {
    console.error("Error in login flow:", error);
    throw toError(error);
  }
};

/**
 * Reset password function
 * @param {Object} options - Password reset options
 * @param {string} options.mobile - Mobile number
 * @param {string} options.newPassword - New password
 * @param {string} options.otpId - OTP ID from forgot password flow
 * @param {string} options.otp - OTP code
 * @returns {Promise<Object>} Response with success status
 */
export const resetPassword = async ({ mobile, newPassword, otpId, otp }) => {
  try {
    // Verify the OTP first
    const verifyResponse = await verifyOTP({
      otpId,
      otp,
    });

    if (!verifyResponse.success) {
      throw new Error(verifyResponse.message || "OTP verification failed");
    }

    // OTP verified, password update handled by client
    return {
      success: true,
      message: "Password reset successful",
    };
  } catch (error) {
    console.error("Error resetting password:", error);
    throw toError(error);
  }
};

/**
 * Forgot password function
 * @param {Object} options - Forgot password options
 * @param {string} options.mobile - Mobile number
 * @returns {Promise<Object>} Response with success status and otpId
 */
export const forgotPassword = async ({ mobile }) => {
  try {
    // Send forgot password OTP
    const sendResponse = await sendOTP({
      mobile,
      useCase: "FORGOT_PASSWORD",
      validity: 10,
    });

    return {
      success: true,
      otpId: sendResponse.data.otpId,
      message: "OTP sent for password reset",
    };
  } catch (error) {
    console.error("Error in forgot password:", error);
    throw toError(error);
  }
};

const otpService = {
  sendOTP,
  verifyOTP,
  verifyOTPByMobile,
  login,
  resetPassword,
  forgotPassword,
  OTP_USE_CASES,
};

export default otpService;
